import React, { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  orderBy,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  limit,
} from "firebase/firestore";
import { signOut } from "firebase/auth";
import { useAuthState } from "react-firebase-hooks/auth";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircle,
  ChevronRight,
  ChevronDown,
  ClipboardList,
  Eye,
  EyeOff,
  Headset,
  Home,
  LogOut,
  Moon,
  Package,
  Smartphone,
  User,
  Bell,
  MessageSquare,
  ExternalLink,
  Clock3,
  Code2,
  X,
  Copy,
  ClipboardCheck,
  CreditCard,
  UserCheck,
  AlertTriangle,
  Send,
  Clock,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { auth, db, mirrorDb, isMirrorTransactionsEnabled } from "../firebase";
import mtnLogo from "../assets/networks/mtn.png";
import airteltigoLogo from "../assets/networks/airteltigo.png";
import telecelLogo from "../assets/networks/telecel.png";
import {
  DEFAULT_PROMO_DISCOUNT_PERCENT,
  getPromoPrice,
  getPromoSavings,
  getPromoTiming,
} from "../utils/promoPricing";

const NETWORKS = [
  { name: "MTN", logo: mtnLogo, shortLabel: "MTN" },
  { name: "Telecel", logo: telecelLogo, shortLabel: "Telecel" },
  { name: "AirtelTigo", logo: airteltigoLogo, shortLabel: "AT" },
];

const ADMIN_SMS_PHONE = "0545454000";
const SUPPORT_NUMBER = "0545454000";
const WHATSAPP_SUPPORT_PHONE = "233545454000";
const WHATSAPP_GROUP_LINK = "https://chat.whatsapp.com/I1Q24yLc5aC4QdIAmjJ7Sn";
const DEFAULT_MOMO_NAME = "KYETECH ADMIN";
const TITLE_LOGO_SRC = "/uklogo.png";
const PROFILE_LOGO_SRC = "/ukapplogo.png";
const USER_NOTIFICATION_SEEN_KEY = "user-notification-last-seen";

const PAYSTACK_PUBLIC_KEY =
  import.meta.env?.VITE_PAYSTACK_KEY ||
  process.env.REACT_APP_PAYSTACK_KEY ||
  "pk_live_8545c70ef4fb392f45389ba119cb921c7911141d";

const MNOTIFY_KEY =
  import.meta.env?.VITE_MNOTIFY_KEY ||
  process.env.REACT_APP_MNOTIFY_KEY ||
  "zov6giqNXoQeyMdHQeqgXkOCa";

const SMS_SENDER_ID =
  import.meta.env?.VITE_SMS_SENDER_ID ||
  process.env.REACT_APP_SMS_SENDER_ID ||
  "KyekyekuTec";

const getPackageCollection = (network) => {
  switch (network) {
    case "MTN":
      return "packages_MTN";
    case "AirtelTigo":
      return "packages_AirtelTigo";
    case "Telecel":
      return "packages_Telecel";
    default:
      return "packages_MTN";
  }
};

const isValidPhone = (value) => /^0\d{9}$/.test(value || "");
const formatGhs = (value) => `GHS ${Number(value || 0).toFixed(2)}`;
const getTimeValue = (stamp) => {
  if (!stamp) return 0;
  if (typeof stamp.toDate === "function") return stamp.toDate().getTime();
  if (typeof stamp.seconds === "number") return stamp.seconds * 1000;
  return 0;
};

async function syncRowsWithMirrorStatus(rows = []) {
  if (!rows.length) {
    return rows;
  }

  const sourceIds = rows.map((row) => row.id).filter(Boolean);
  if (!sourceIds.length) {
    return rows;
  }

  const sourceIdBatches = [];
  for (let index = 0; index < sourceIds.length; index += 10) {
    sourceIdBatches.push(sourceIds.slice(index, index + 10));
  }

  const mirrorSnapshots = await Promise.all(
    sourceIdBatches.map((batch) =>
      getDocs(query(collection(mirrorDb, "credentials"), where("sourceDocId", "in", batch), limit(QUERY_LIMIT)))
    )
  );

  const mirrorBySourceId = new Map();
  mirrorSnapshots.forEach((snapshot) => {
    snapshot.docs.forEach((item) => {
      const data = item.data();
      if (data.sourceDocId) {
        mirrorBySourceId.set(data.sourceDocId, data);
      }
    });
  });

  const updates = [];
  const mergedRows = rows.map((row) => {
    const mirrorMatch = mirrorBySourceId.get(row.id);
    const mirrorStatus = String(mirrorMatch?.status || "").toLowerCase();
    const localStatus = String(row.status || "pending").toLowerCase();

    if (mirrorStatus && mirrorStatus !== localStatus) {
      updates.push(
        updateDoc(doc(db, "credentials", row.id), {
          status: mirrorStatus,
          updatedAt: serverTimestamp(),
        }).catch(() => undefined)
      );

      return {
        ...row,
        status: mirrorStatus,
      };
    }

    return row;
  });

  if (updates.length) {
    await Promise.all(updates);
  }

  return mergedRows;
}

/* ================= CACHE SETTINGS ================= */
const CACHE_KEYS = {
  PACKAGES_PREFIX: "user_packages_",
  HISTORY: "user_history_cache",
  SETTINGS: "user_settings_cache",
  CACHE_TIME: "user_cache_time",
};
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const QUERY_LIMIT = 50; // Limit packages and history

function StatusBadge({ status }) {
  const map = {
    pending: "bg-amber-500/15 text-amber-400",
    paid_pending_confirmation: "bg-amber-500/15 text-amber-400",
    processing: "bg-sky-500/15 text-sky-400",
    completed: "bg-green-500/15 text-green-400",
    approved: "bg-green-500/15 text-green-400",
    rejected: "bg-red-500/15 text-red-400",
  };

  const labels = {
    paid_pending_confirmation: "Pending",
  };

  return (
    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${map[status] || "bg-white/10 text-white"}`}>
      {labels[status] || status || "pending"}
    </span>
  );
}

const NetworkStatusBadge = ({ status }) => {
  const normalized = String(status || "").toLowerCase();
  const map = {
    good: { color: "bg-green-500", animation: "animate-pulse" },
    slow: { color: "bg-yellow-400", animation: "animate-pulse" },
    bad: { color: "bg-red-500", animation: "animate-ping" },
    down: { color: "bg-red-500", animation: "animate-ping" },
  };

  const current = map[normalized] || { color: "bg-gray-400", animation: "" };

  return (
    <span
      title={(normalized === "down" ? "BAD" : normalized.toUpperCase()) || "UNKNOWN"}
      className={`relative inline-flex h-2.5 w-2.5 rounded-full md:h-3 md:w-3 ${current.color} ${current.animation} md:animate-none`}
    />
  );
};

function buildTopupHistory(walletRows = [], manualRows = []) {
  const paystackTopups = walletRows
    .filter((item) => item.type === "topup")
    .map((item) => ({
      id: `wallet-${item.id}`,
      amount: item.amount,
      status: item.status || "completed",
      methodLabel: item.method === "paystack" ? "Paystack Payment" : "Wallet Credit",
      title: "Wallet Top-Up",
      createdAt: item.createdAt,
      source: "wallet",
    }));

  const manualTopups = manualRows.map((item) => ({
    id: `manual-${item.id}`,
    amount: item.amount,
    status: item.status || "pending",
    methodLabel: "Direct MoMo Deposit",
    title: "Manual Top-Up",
    createdAt: item.paidAt || item.createdAt,
    paymentReference: item.paymentReference,
    source: "manual",
  }));

  return [...manualTopups, ...paystackTopups].sort(
    (a, b) => getTimeValue(b.createdAt) - getTimeValue(a.createdAt)
  );
}

function BottomNav({ activeTab, setActiveTab, isDark }) {
  const items = [
    { id: "home", label: "Home", icon: <Home size={20} /> },
    { id: "purchases", label: "Purchases", icon: <ClipboardList size={20} /> },
    { id: "support", label: "Support", icon: <Headset size={20} /> },
    { id: "profile", label: "Profile", icon: <User size={20} /> },
  ];

  return (
    <motion.div
      initial={{ y: 100 }}
      animate={{ y: 0 }}
      transition={{ delay: 0.8, type: "spring", stiffness: 300, damping: 30 }}
      className={`fixed bottom-0 left-0 right-0 z-50 border-t px-4 py-3 backdrop-blur-2xl shadow-2xl ${
        isDark
          ? "border-slate-700/50 bg-gradient-to-t from-slate-900/95 to-slate-800/90"
          : "border-white/50 bg-gradient-to-t from-white/95 to-slate-50/90"
      }`}
    >
      <div className="mx-auto flex w-full max-w-md items-center justify-around">
        {items.map((item) => {
          const active = activeTab === item.id;
          return (
            <motion.button
              key={item.id}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setActiveTab(item.id)}
              className={`relative flex flex-col items-center gap-1 rounded-2xl px-4 py-3 text-xs font-bold transition-all duration-300 ${
                active
                  ? `bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-xl ${
                      isDark ? "shadow-blue-500/25" : "shadow-blue-500/30"
                    }`
                  : isDark
                    ? "text-slate-400 hover:text-white hover:bg-slate-800/50"
                    : "text-slate-500 hover:text-slate-900 hover:bg-white/50"
              }`}
            >
              {active && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500 to-blue-600"
                  initial={false}
                  transition={{ type: "spring", stiffness: 500, damping: 30 }}
                />
              )}
              <div className="relative z-10">
                {item.icon}
              </div>
              <span className="relative z-10">{item.label}</span>
              {active && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-white"
                />
              )}
            </motion.button>
          );
        })}
      </div>
    </motion.div>
  );
}

export default function UserDashboard() {
  const navigate = useNavigate();
  const [user, authLoading] = useAuthState(auth);

  const [theme, setTheme] = useState(localStorage.getItem("user-theme") || "light");
  const [balanceHidden, setBalanceHidden] = useState(false);
  const [profile, setProfile] = useState(null);
  const [activeTab, setActiveTab] = useState("home");
  const [selectedNetwork, setSelectedNetwork] = useState("MTN");
  const [packages, setPackages] = useState([]);
  const [selectedPackageId, setSelectedPackageId] = useState("");
  const [beneficiary, setBeneficiary] = useState("");
  const [history, setHistory] = useState([]);
  const [packagesActive, setPackagesActive] = useState(true);
  const [networkStatus, setNetworkStatus] = useState({});
  const [promoActive, setPromoActive] = useState(false);
  const [promoDiscountPercent, setPromoDiscountPercent] = useState(DEFAULT_PROMO_DISCOUNT_PERCENT);
  const [promoStartAt, setPromoStartAt] = useState(null);
  const [promoEndAt, setPromoEndAt] = useState(null);
  const [promoNow, setPromoNow] = useState(new Date());
  const [loadingPage, setLoadingPage] = useState(true);
  const [loadingPurchase, setLoadingPurchase] = useState(false);
  const [loadingTopup, setLoadingTopup] = useState(false);
  const [serviceTab, setServiceTab] = useState("data"); // "data", "airtime", or "topup"
  const [topupAmount, setTopupAmount] = useState(0);
  const [topupMethod, setTopupMethod] = useState("paystack"); // "paystack" or "manual"
  const [topupManualReason, setTopupManualReason] = useState("");
  const [manualReference, setManualReference] = useState("");
  const [manualPaymentNumber, setManualPaymentNumber] = useState(ADMIN_SMS_PHONE);
  const [manualPaymentName, setManualPaymentName] = useState(DEFAULT_MOMO_NAME);
  const [copiedField, setCopiedField] = useState(null);
  const [purchaseTab, setPurchaseTab] = useState("data"); // "data", "airtime", or "topups" in purchases section
  const [topups, setTopups] = useState([]);
  const [showPackagePicker, setShowPackagePicker] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [failedPurchaseDraft, setFailedPurchaseDraft] = useState(null);
  const [profileFormName, setProfileFormName] = useState("");
  const [profileFormPhone, setProfileFormPhone] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [loadingAgentUpgrade, setLoadingAgentUpgrade] = useState(false);
  const [showProfileExtra, setShowProfileExtra] = useState(false);
  const [airtimeRequestNumber, setAirtimeRequestNumber] = useState("");
  const [airtimeAmount, setAirtimeAmount] = useState("");
  const [airtimeNetwork, setAirtimeNetwork] = useState("MTN");
  const [loadingAirtimePurchase, setLoadingAirtimePurchase] = useState(false);

  const isDark = theme === "dark";

  const showError = (message) => {
    window.alert("❌ Error: " + message);
  };

  const showSuccess = (message) => {
    window.alert("✓ Success: " + message);
  };

  useEffect(() => {
    localStorage.setItem("user-theme", theme);
  }, [theme]);

  async function loadNotifications() {
    setLoadingNotifications(true);
    try {
      const snap = await getDocs(
        query(collection(db, "broadcastNotifications"), orderBy("createdAt", "desc"), limit(30))
      );
      const rows = snap.docs
        .map((entry) => ({ id: entry.id, ...entry.data() }))
        .filter((entry) => ["all", "agent", "agents"].includes(String(entry.target || "all").toLowerCase()));

      const lastSeen = Number(localStorage.getItem(USER_NOTIFICATION_SEEN_KEY) || 0);
      setUnreadNotifications(rows.filter((entry) => getTimeValue(entry.createdAt) > lastSeen).length);
      setNotifications(rows);
      return rows;
    } catch {
      setNotifications([]);
      return [];
    } finally {
      setLoadingNotifications(false);
    }
  }

  const openNotifications = async () => {
    const rows = await loadNotifications();
    const newest = rows.length ? Math.max(...rows.map((item) => getTimeValue(item.createdAt))) : 0;
    localStorage.setItem(USER_NOTIFICATION_SEEN_KEY, String(newest));
    setUnreadNotifications(0);
    setShowNotifications(true);
  };

  useEffect(() => {
    void loadNotifications();
  }, []);

  useEffect(() => {
    if (!promoActive) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setPromoNow(new Date());
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [promoActive]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/");
    }
  }, [authLoading, navigate, user]);

  useEffect(() => {
    async function loadAgentWorkspace() {
      if (!user) {
        return;
      }

      setLoadingPage(true);
      const userRef = doc(db, "users", user.uid);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists() || userSnap.data().role !== "user") {
        await signOut(auth);
        navigate("/");
        return;
      }

      const userData = userSnap.data();
      setProfile({ id: user.uid, email: user.email, ...userData });

      // Check cache for settings
      const cachedTime = localStorage.getItem(CACHE_KEYS.CACHE_TIME);
      const cacheValid = cachedTime && Date.now() - parseInt(cachedTime) < CACHE_DURATION;
      
      if (cacheValid) {
        const cachedSettings = localStorage.getItem(CACHE_KEYS.SETTINGS);
        if (cachedSettings) {
          const settingsData = JSON.parse(cachedSettings);
          setPackagesActive(settingsData.packagesActive ?? true);
          setPromoActive(settingsData.promoActive ?? false);
          setPromoDiscountPercent(settingsData.promoDiscountPercent ?? DEFAULT_PROMO_DISCOUNT_PERCENT);
          setPromoStartAt(settingsData.promoStartAt ?? null);
          setPromoEndAt(settingsData.promoEndAt ?? null);

          const cachedPayment = localStorage.getItem("user_payment_settings_cache");
          if (cachedPayment) {
            try {
              const paymentData = JSON.parse(cachedPayment);
              setManualPaymentNumber(paymentData.paymentNumber || ADMIN_SMS_PHONE);
              setManualPaymentName(paymentData.paymentName || DEFAULT_MOMO_NAME);
            } catch {
              localStorage.removeItem("user_payment_settings_cache");
            }
          }

          setLoadingPage(false);
          return;
        }
      }

      // Fetch fresh settings
      const settingsSnap = await getDoc(doc(db, "settings", "packages"));
      if (settingsSnap.exists()) {
        const settingsData = settingsSnap.data();
        setPackagesActive(settingsData.packagesActive ?? true);
        setPromoActive(settingsData.promoActive ?? false);
        setPromoDiscountPercent(settingsData.promoDiscountPercent ?? DEFAULT_PROMO_DISCOUNT_PERCENT);
        setPromoStartAt(settingsData.promoStartAt ?? null);
        setPromoEndAt(settingsData.promoEndAt ?? null);
        
        // Cache the settings
        localStorage.setItem(CACHE_KEYS.SETTINGS, JSON.stringify(settingsData));
        localStorage.setItem(CACHE_KEYS.CACHE_TIME, Date.now().toString());
      }

      const networkSnap = await getDoc(doc(db, "network_status", "current"));
      if (networkSnap.exists()) {
        setNetworkStatus(networkSnap.data());
      }

      const paymentSnap = await getDoc(doc(db, "settings", "payment"));
      if (paymentSnap.exists()) {
        const paymentData = paymentSnap.data();
        setManualPaymentNumber(paymentData.paymentNumber || ADMIN_SMS_PHONE);
        setManualPaymentName(paymentData.paymentName || DEFAULT_MOMO_NAME);
        localStorage.setItem("user_payment_settings_cache", JSON.stringify(paymentData));
      }

      setLoadingPage(false);
    }

    loadAgentWorkspace();
  }, [navigate, user]);

  useEffect(() => {
    if (!user) {
      return undefined;
    }

    const unsubscribe = onSnapshot(doc(db, "settings", "payment"), (paymentSnap) => {
      if (!paymentSnap.exists()) {
        setManualPaymentNumber(ADMIN_SMS_PHONE);
        setManualPaymentName(DEFAULT_MOMO_NAME);
        localStorage.removeItem("user_payment_settings_cache");
        return;
      }

      const paymentData = paymentSnap.data();
      const nextPaymentSettings = {
        paymentNumber: paymentData.paymentNumber || ADMIN_SMS_PHONE,
        paymentName: paymentData.paymentName || DEFAULT_MOMO_NAME,
      };

      setManualPaymentNumber(nextPaymentSettings.paymentNumber);
      setManualPaymentName(nextPaymentSettings.paymentName);
      localStorage.setItem("user_payment_settings_cache", JSON.stringify(nextPaymentSettings));
    });

    return () => unsubscribe();
  }, [user]);

  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, "network_status", "current"), (networkSnap) => {
      if (networkSnap.exists()) {
        setNetworkStatus(networkSnap.data());
      } else {
        setNetworkStatus({});
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!profile) {
      return;
    }

    setProfileFormName(profile.fullName || profile.businessName || "");
    setProfileFormPhone(profile.phone || "");
  }, [profile]);

  useEffect(() => {
    const colName = getPackageCollection(selectedNetwork);
    const cacheKey = CACHE_KEYS.PACKAGES_PREFIX + selectedNetwork;

    // Always hydrate from local cache first for instant UI.
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed)) {
          setPackages(parsed);
        }
      } catch {
        localStorage.removeItem(cacheKey);
      }
    }

    const unsubscribe = onSnapshot(collection(db, colName), (snap) => {
      const pkgs = snap.docs.map((item) => ({ id: item.id, ...item.data() }));
      setPackages(pkgs);
      localStorage.setItem(cacheKey, JSON.stringify(pkgs));
      localStorage.setItem(CACHE_KEYS.CACHE_TIME, Date.now().toString());
    });

    return () => unsubscribe();
  }, [selectedNetwork]);

  useEffect(() => {
    if (!user) {
      return undefined;
    }

    // Hydrate quickly from cache before subscribing in realtime.
    const cached = localStorage.getItem(CACHE_KEYS.HISTORY);
    if (cached) {
      try {
        setHistory(JSON.parse(cached));
      } catch {
        localStorage.removeItem(CACHE_KEYS.HISTORY);
      }
    }

    const historyQuery = query(
      collection(db, "credentials"),
      where("agentId", "==", user.uid),
      limit(QUERY_LIMIT)
    );

    const unsubscribe = onSnapshot(historyQuery, (snap) => {
      void (async () => {
        const orders = snap.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .sort((a, b) => {
            const aTime = a.assignedAt?.seconds || 0;
            const bTime = b.assignedAt?.seconds || 0;
            return bTime - aTime;
          });

        const syncedOrders = await syncRowsWithMirrorStatus(orders);
        setHistory(syncedOrders);
        localStorage.setItem(CACHE_KEYS.HISTORY, JSON.stringify(syncedOrders));
        localStorage.setItem(CACHE_KEYS.CACHE_TIME, Date.now().toString());
      })();
    });

    return unsubscribe;
  }, [user]);

  useEffect(() => {
    if (!user) {
      return undefined;
    }

    const cachedTime = localStorage.getItem(CACHE_KEYS.CACHE_TIME);
    if (cachedTime && Date.now() - parseInt(cachedTime) < CACHE_DURATION) {
      const cached = localStorage.getItem("agent_topups_cache");
      if (cached) {
        setTopups(JSON.parse(cached));
      }
    }

    let latestWalletTopups = [];
    let latestManualRequests = [];

    const syncTopupHistory = () => {
      const topupList = buildTopupHistory(latestWalletTopups, latestManualRequests);
      setTopups(topupList);
      localStorage.setItem("agent_topups_cache", JSON.stringify(topupList));
      localStorage.setItem(CACHE_KEYS.CACHE_TIME, Date.now().toString());
    };

    const unsubscribeWallet = onSnapshot(
      query(
        collection(db, "walletTransactions"),
        where("agentId", "==", user.uid),
        where("type", "==", "topup"),
        limit(QUERY_LIMIT)
      ),
      (snapshot) => {
        latestWalletTopups = snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
        syncTopupHistory();
      }
    );

    const unsubscribeManual = onSnapshot(
      query(collection(db, "topupRequests"), where("agentId", "==", user.uid), limit(QUERY_LIMIT)),
      (snapshot) => {
        latestManualRequests = snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
        syncTopupHistory();
      }
    );

    return () => {
      unsubscribeWallet();
      unsubscribeManual();
    };
  }, [user]);

  const promoTiming = useMemo(() => {
    return getPromoTiming(
      {
        promoActive,
        promoStartAt,
        promoEndAt,
      },
      promoNow
    );
  }, [promoActive, promoEndAt, promoNow, promoStartAt]);

  const effectivePromoActive = promoTiming.isDiscountActive;

  const filteredPackages = useMemo(() => {
    return packages
      .filter((pkg) => !pkg.network || pkg.network === selectedNetwork)
      .map((pkg) => {
        const basePrice = Number(pkg.userPrice ?? pkg.price ?? 0);
        return {
          ...pkg,
          promoPrice: getPromoPrice(basePrice, effectivePromoActive, promoDiscountPercent),
          promoSavings: getPromoSavings(basePrice, effectivePromoActive, promoDiscountPercent),
        };
      })
      .sort((left, right) => left.promoPrice - right.promoPrice);
  }, [effectivePromoActive, packages, promoDiscountPercent, selectedNetwork]);

  const selectedPackage = filteredPackages.find((pkg) => pkg.id === selectedPackageId) || null;
  const selectedNetworkStatus = networkStatus?.[selectedNetwork]?.status || "good";

  async function refreshProfile() {
    if (!user) {
      return;
    }

    const snap = await getDoc(doc(db, "users", user.uid));
    if (snap.exists()) {
      const data = snap.data();
      setProfile({ id: user.uid, email: user.email, ...data });
    }
  }

  async function refreshHistory() {
    if (!user) {
      return;
    }

    // Clear cache for history
    localStorage.removeItem(CACHE_KEYS.HISTORY);
    
    const snap = await getDocs(
      query(
        collection(db, "credentials"),
        where("agentId", "==", user.uid),
        limit(QUERY_LIMIT)
      )
    );

    const orders = snap.docs.map((item) => ({ id: item.id, ...item.data() }))
      .sort((a, b) => {
        const aTime = a.assignedAt?.seconds || 0;
        const bTime = b.assignedAt?.seconds || 0;
        return bTime - aTime;
      });
    const syncedOrders = await syncRowsWithMirrorStatus(orders);
    setHistory(syncedOrders);
    
    // Re-cache
    localStorage.setItem(CACHE_KEYS.HISTORY, JSON.stringify(syncedOrders));
  }

  async function refreshTopups() {
    if (!user) {
      return;
    }

    // Clear cache for topups
    localStorage.removeItem("agent_topups_cache");

    const [walletSnap, manualSnap] = await Promise.all([
      getDocs(
        query(
          collection(db, "walletTransactions"),
          where("agentId", "==", user.uid),
          where("type", "==", "topup"),
          limit(QUERY_LIMIT)
        )
      ),
      getDocs(query(collection(db, "topupRequests"), where("agentId", "==", user.uid), limit(QUERY_LIMIT))),
    ]);

    const topupList = buildTopupHistory(
      walletSnap.docs.map((item) => ({ id: item.id, ...item.data() })),
      manualSnap.docs.map((item) => ({ id: item.id, ...item.data() }))
    );
    setTopups(topupList);
    
    // Re-cache
    localStorage.setItem("agent_topups_cache", JSON.stringify(topupList));
  }

  async function handlePurchase() {
    showError("");
    showSuccess("");
    setFailedPurchaseDraft(null);

    if (!packagesActive) {
      showError("Package purchasing is currently offline.");
      return;
    }

    if (!selectedPackage) {
      showError("Select a package first.");
      return;
    }

    if (selectedPackage.outOfStock) {
      showError("Selected package is out of stock.");
      return;
    }

    if (!isValidPhone(beneficiary)) {
      showError("Enter a valid beneficiary phone number.");
      return;
    }

    if (["bad", "down"].includes(String(selectedNetworkStatus || "").toLowerCase())) {
      showError(`${selectedNetwork} network is currently bad.`);
      return;
    }

    const cleanBeneficiary = beneficiary.trim();
    const orderAmount = Number(selectedPackage.promoPrice);

    if (!Number.isFinite(orderAmount) || orderAmount <= 0) {
      showError("Selected package has an invalid price. Contact admin to update package pricing.");
      return;
    }

    setLoadingPurchase(true);
    let createdOrderId = "";

    try {
      await runTransaction(db, async (transaction) => {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await transaction.get(userRef);

        if (!userSnap.exists()) {
          throw new Error("Agent profile not found.");
        }

        const walletBalance = Number(userSnap.data().walletBalance || 0);
        if (!Number.isFinite(walletBalance)) {
          throw new Error("Wallet balance is invalid. Please contact admin.");
        }

        if (walletBalance < orderAmount) {
          throw new Error("Insufficient wallet balance.");
        }

        const nextBalance = Number((walletBalance - orderAmount).toFixed(2));
        const orderRef = doc(collection(db, "credentials"));
        createdOrderId = orderRef.id;
        const walletRef = doc(collection(db, "walletTransactions"));

        transaction.update(userRef, {
          walletBalance: nextBalance,
          updatedAt: serverTimestamp(),
        });

        transaction.set(orderRef, {
          assignedTo: cleanBeneficiary,
          network: selectedNetwork,
          status: "pending",
          type: "data",
          balanceBefore: walletBalance,
          balanceAfter: nextBalance,
          totalPaid: orderAmount,
          assignedAt: serverTimestamp(),
          source: "agent",
          agentId: user.uid,
          agentEmail: profile?.email || user.email,
          agentName: profile?.fullName || profile?.businessName || user.email,
        });

        transaction.set(walletRef, {
          agentId: user.uid,
          agentEmail: profile?.email || user.email,
          agentName: profile?.fullName || profile?.businessName || user.email,
          amount: -orderAmount,
          balanceAfter: nextBalance,
          type: "purchase",
          method: "wallet",
          status: "completed",
          packageId: selectedPackage.id,
          beneficiary: cleanBeneficiary,
          network: selectedNetwork,
          note: "Agent wallet data purchase",
          createdAt: serverTimestamp(),
        });
      });

      if (createdOrderId && (await isMirrorTransactionsEnabled())) {
        try {
          await setDoc(doc(mirrorDb, "credentials", createdOrderId), {
            sourceDocId: createdOrderId,
            assignedTo: cleanBeneficiary,
            network: selectedNetwork,
            status: "pending",
            type: "data",
            packageId: selectedPackage.id,
            totalPaid: orderAmount,
            source: "agent",
            agentId: user.uid,
            agentEmail: profile?.email || user.email,
            agentName: profile?.fullName || profile?.businessName || user.email,
            assignedAt: serverTimestamp(),
          });
        } catch {
          // Keep purchase successful even if mirror write fails.
        }
      }

      showSuccess(`Purchase placed successfully for ${cleanBeneficiary}.`);
      setBeneficiary("");
      setSelectedPackageId("");
      await refreshProfile();
      await refreshHistory();
      setActiveTab("purchases");
    } catch (purchaseError) {
      const code = String(purchaseError?.code || "").toLowerCase();
      const message = String(purchaseError?.message || "");
      const purchaseDraft = {
        network: selectedNetwork,
        packageId: selectedPackage?.id || "",
        beneficiary: cleanBeneficiary,
        amount: orderAmount,
      };

      if (code.includes("permission-denied")) {
        showError("Purchase blocked by Firestore rules. Ask admin to grant agent write access.");
      } else if (code.includes("resource-exhausted") || /quota\s*exceeded/i.test(message)) {
        setFailedPurchaseDraft(purchaseDraft);
        showError("Purchase failed because Firestore quota is exceeded. Use Send to Admin below to complete this order manually.");
      } else if (code.includes("unavailable")) {
        showError("Purchase service is temporarily unavailable. Please try again shortly.");
      } else {
        showError(message || "Unable to place purchase right now.");
      }
    } finally {
      setLoadingPurchase(false);
    }
  }

  async function handleAirtimePurchase() {
    showError("");
    showSuccess("");

    const cleanRequestNumber = String(airtimeRequestNumber || "").trim();
    const airtimePrice = Number(airtimeAmount || 0);

    if (!cleanRequestNumber) {
      showError("Enter an airtime request number.");
      return;
    }

    if (airtimePrice <= 0 || !Number.isFinite(airtimePrice)) {
      showError("Enter a valid airtime amount.");
      return;
    }

    if (!airtimeNetwork) {
      showError("Select a network.");
      return;
    }

    setLoadingAirtimePurchase(true);

    try {
      await runTransaction(db, async (transaction) => {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await transaction.get(userRef);

        if (!userSnap.exists()) {
          throw new Error("User profile not found.");
        }

        const walletBalance = Number(userSnap.data().walletBalance || 0);
        if (!Number.isFinite(walletBalance)) {
          throw new Error("Wallet balance is invalid. Please contact admin.");
        }

        if (walletBalance < airtimePrice) {
          throw new Error("Insufficient wallet balance for this airtime purchase.");
        }

        const nextBalance = Number((walletBalance - airtimePrice).toFixed(2));
        const orderRef = doc(collection(db, "credentials"));
        const walletRef = doc(collection(db, "walletTransactions"));

        transaction.update(userRef, {
          walletBalance: nextBalance,
          updatedAt: serverTimestamp(),
        });

        transaction.set(orderRef, {
          requestNumber: cleanRequestNumber,
          network: airtimeNetwork,
          status: "pending",
          type: "airtime",
          balanceBefore: walletBalance,
          balanceAfter: nextBalance,
          assignedAt: serverTimestamp(),
          source: "user",
          agentId: user.uid,
          agentEmail: profile?.email || user.email,
          agentName: profile?.fullName || profile?.businessName || user.email,
        });

        transaction.set(walletRef, {
          agentId: user.uid,
          agentEmail: profile?.email || user.email,
          agentName: profile?.fullName || profile?.businessName || user.email,
          amount: -airtimePrice,
          balanceAfter: nextBalance,
          type: "purchase",
          method: "wallet",
          status: "completed",
          requestNumber: cleanRequestNumber,
          network: airtimeNetwork,
          note: "User wallet airtime purchase",
          createdAt: serverTimestamp(),
        });
      });

      showSuccess(`Airtime purchase successful! GHS ${airtimePrice.toFixed(2)} deducted for ${airtimeNetwork}.`);
      setAirtimeRequestNumber("");
      setAirtimeAmount("");
      setAirtimeNetwork("MTN");
      await refreshProfile();
      await refreshHistory();
      setActiveTab("purchases");
    } catch (airtimeError) {
      const code = String(airtimeError?.code || "").toLowerCase();
      const message = String(airtimeError?.message || "");

      if (code.includes("permission-denied")) {
        showError("Airtime purchase blocked by Firestore rules. Ask admin to grant user write access.");
      } else if (code.includes("resource-exhausted") || /quota\s*exceeded/i.test(message)) {
        showError("Airtime purchase failed because Firestore quota is exceeded. Please try again shortly.");
      } else if (code.includes("unavailable")) {
        showError("Airtime service is temporarily unavailable. Please try again shortly.");
      } else {
        showError(message || "Unable to complete airtime purchase right now.");
      }
    } finally {
      setLoadingAirtimePurchase(false);
    }
  }

  async function handleSimSetup() {
    const defaultNumber = localStorage.getItem("agent-sim-number") || profile?.phone || "";
    const value = window.prompt("Enter your SIM number (10 digits, starts with 0)", defaultNumber);

    if (value === null) {
      return;
    }

    const cleanValue = value.trim();
    if (!isValidPhone(cleanValue)) {
      showError("Invalid phone number. Use 10 digits starting with 0.");
      return;
    }

    try {
      localStorage.setItem("agent-sim-number", cleanValue);
      await updateDoc(doc(db, "users", profile.id), {
        phone: cleanValue,
        updatedAt: serverTimestamp(),
      });
      await refreshProfile();
      showSuccess("SIM number updated successfully.");
    } catch {
      showError("Unable to save SIM number right now.");
    }
  }

  async function handleProfileUpdate() {
    showSuccess("");
    showError("");

    const cleanName = String(profileFormName || "").trim();
    const cleanPhone = String(profileFormPhone || "").trim();

    if (!cleanName) {
      showError("Enter your full name.");
      return;
    }

    if (!isValidPhone(cleanPhone)) {
      showError("Enter a valid phone number (10 digits, starts with 0).");
      return;
    }

    setSavingProfile(true);
    try {
      await updateDoc(doc(db, "users", profile.id), {
        fullName: cleanName,
        phone: cleanPhone,
        updatedAt: serverTimestamp(),
      });

      localStorage.setItem("agent-sim-number", cleanPhone);
      await refreshProfile();
      showSuccess("Profile updated successfully.");
    } catch (profileError) {
      showError(profileError?.message || "Unable to update profile right now.");
    } finally {
      setSavingProfile(false);
    }
  }

  async function handleTopupWithPaystack() {
    showError("");
    showSuccess("");

    if (topupAmount <= 0) {
      showError("Enter a valid top-up amount.");
      return;
    }

    if (!profile?.email && !user?.email) {
      showError("Email not found. Unable to proceed.");
      return;
    }

    setLoadingTopup(true);

    try {
      if (!window?.PaystackPop?.setup) {
        throw new Error("Payment service is not available right now. Please refresh and try again.");
      }

      const onPaystackSuccess = (res) => {
        const reference =
          res?.reference ||
          res?.trxref ||
          res?.trans ||
          "";

        if (!reference) {
          setLoadingTopup(false);
          showError("Payment callback received without reference. Please contact admin.");
          return;
        }

        void handleTopupSuccess(reference);
      };

      const paystackFeePercent = 0.02; // 2% fee
      const amountInCents = Math.round(Number(topupAmount) * 100);
      const feeInCents = Math.round(amountInCents * paystackFeePercent);
      const totalAmountInCents = amountInCents + feeInCents;

      const handler = window.PaystackPop.setup({
        key: PAYSTACK_PUBLIC_KEY,
        email: profile?.email || user.email,
        amount: totalAmountInCents,
        currency: "GHS",
        ref: `TOPUP-${user.uid}-${Date.now()}`,
        onClose: () => {
          setLoadingTopup(false);
          showError("Top-up payment was cancelled.");
        },
        callback: onPaystackSuccess,
      });

      if (!handler?.openIframe) {
        throw new Error("Unable to open payment window. Please try again.");
      }

      handler.openIframe();
    } catch (paystackError) {
      const rawMessage = String(paystackError?.message || "");
      const isQuotaExceeded = /quota\s*exceeded/i.test(rawMessage);

      setLoadingTopup(false);

      if (isQuotaExceeded) {
        setTopupMethod("manual");
        showError("Paystack quota exceeded. Please use Request (Admin) for now or update your Paystack public key.");
        return;
      }

      showError(rawMessage || "Unable to start payment. Please try again.");
    }
  }

  async function handleTopupSuccess(ref) {
    try {
      const userRef = doc(db, "users", user.uid);
      const currentBalance = Number(profile?.walletBalance || 0);
      const newBalance = currentBalance + topupAmount;
      const paystackFeeAmount = Number((topupAmount * 0.02).toFixed(2));

      await updateDoc(userRef, {
        walletBalance: newBalance,
        updatedAt: serverTimestamp(),
      });

      await addDoc(collection(db, "walletTransactions"), {
        agentId: user.uid,
        amount: topupAmount,
        paystackFee: paystackFeeAmount,
        totalCharged: topupAmount + paystackFeeAmount,
        balanceBefore: currentBalance,
        balanceAfter: newBalance,
        type: "topup",
        method: "paystack",
        paystackRef: ref,
        createdAt: serverTimestamp(),
      });

      await refreshProfile();
      await refreshTopups();
      showSuccess(`Top-up successful! GHS ${topupAmount.toFixed(2)} added to wallet.`);
      setTopupAmount(0);
      setLoadingTopup(false);
    } catch (err) {
      const errCode = String(err?.code || "").toLowerCase();
      const errMessage = String(err?.message || "");
      const quotaExceeded =
        errCode.includes("resource-exhausted") ||
        /quota\s*exceeded/i.test(errMessage);

      if (quotaExceeded) {
        showError("Payment may have completed, but Firestore quota exceeded while saving. Contact admin with your Paystack reference.");
      } else {
        showError("Top-up failed. Please contact admin.");
      }
      setLoadingTopup(false);
    }
  }

  function generateManualReference() {
    const letter = String.fromCharCode(65 + Math.floor(Math.random() * 26)); // A-Z
    const numbers = Math.floor(Math.random() * 1000).toString().padStart(3, '0'); // 000-999
    const ref = `${letter}${numbers}`;
    setManualReference(ref);
    return ref;
  }

  function normalizePhone(phoneNumber) {
    const raw = String(phoneNumber || "").replace(/\s+/g, "");
    if (raw.startsWith("0") && raw.length === 10) {
      return `233${raw.slice(1)}`;
    }
    return raw;
  }

  async function sendAdminSms(message) {
    const recipient = normalizePhone(ADMIN_SMS_PHONE);
    if (!recipient) {
      throw new Error("Invalid admin phone number for SMS alert.");
    }

    const response = await fetch(`https://api.mnotify.com/api/sms/quick?key=${encodeURIComponent(MNOTIFY_KEY)}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        recipient: [recipient],
        sender: SMS_SENDER_ID,
        message: String(message || "").trim(),
      }),
    });

    const data = await response.json().catch(() => null);

    if (!response.ok || (data && !data.success && String(data.status || "").toLowerCase() !== "success")) {
      console.error("Admin SMS failure:", response.status, data);
      throw new Error(data?.message || "Unable to send SMS alert to admin.");
    }
  }

  async function handleManualPaymentConfirmation() {
    showError("");
    showSuccess("");

    if (topupAmount <= 0) {
      showError("Enter a valid top-up amount before confirming payment.");
      return;
    }

    if (!String(manualReference || "").trim()) {
      showError("Generate a payment reference before tapping I Have Paid.");
      return;
    }

    setLoadingTopup(true);

    try {
      const paymentReference = manualReference.trim();
      const agentName = profile?.fullName || profile?.businessName || user?.email || "Agent";

      await addDoc(collection(db, "topupRequests"), {
        agentId: user.uid,
        agentEmail: profile?.email || user.email,
        agentName,
        amount: topupAmount,
        reason: topupManualReason.trim(),
        status: "paid_pending_confirmation",
        paymentChannel: "momo_direct_deposit",
        paymentReference,
        momoNumber: manualPaymentNumber,
        momoName: manualPaymentName,
        paidAt: serverTimestamp(),
        createdAt: serverTimestamp(),
      });

       const smsMessage = [
        "MANUAL TOPUP ALERT",
        `AGENT NAME: ${agentName}`,
        `AMOUNT: GHS ${Number(topupAmount).toFixed(2)}`,
        `REFERENCE: ${paymentReference}`,
      ].join("\n");


      await sendAdminSms(smsMessage);

      showSuccess("Payment confirmation sent to admin by SMS. Wallet will be credited after verification.");
      setTopupAmount(0);
      setTopupManualReason("");
      setManualReference("");
      setLoadingTopup(false);
    } catch (confirmError) {
      showError(confirmError.message || "Unable to confirm manual payment right now.");
      setLoadingTopup(false);
    }
  }

  async function handleAgentUpgrade() {
    showError("");
    showSuccess("");
    setLoadingAgentUpgrade(true);

    const AGENT_REGISTRATION_FEE = 30;

    try {
      if (!window?.PaystackPop || typeof window.PaystackPop.setup !== "function") {
        throw new Error("Payment service is not available right now. Please refresh and try again.");
      }

      const onPaystackSuccess = (response) => {
        const reference = response?.reference || response?.trxref || response?.trans || "";

        if (!reference) {
          setLoadingAgentUpgrade(false);
          showError("Payment callback received without reference. Please contact admin.");
          return;
        }

        // Handle the async upgrade in a separate function
        void (async () => {
          try {
            await updateDoc(doc(db, "users", user.uid), {
              role: "agent",
              paid: true,
              approved: true,
              onboardingFee: AGENT_REGISTRATION_FEE,
              onboardingReference: reference,
              updatedAt: serverTimestamp(),
            });

            await addDoc(collection(db, "walletTransactions"), {
              agentId: user.uid,
              amount: 0,
              type: "registration",
              note: "Agent onboarding payment completed",
              reference,
              createdAt: serverTimestamp(),
            });

            await refreshProfile();

            showSuccess("Congratulations! You are now an agent. Redirecting to agent dashboard...");
            navigate("/agent/dashboard");
          } catch (upgradeError) {
            showError("Payment completed but upgrade failed. Please contact admin with reference: " + reference);
          } finally {
            setLoadingAgentUpgrade(false);
          }
        })();
      };

      const handler = window.PaystackPop.setup({
        key: PAYSTACK_PUBLIC_KEY,
        email: profile?.email || user.email,
        amount: Math.round(AGENT_REGISTRATION_FEE * 100),
        currency: "GHS",
        ref: `AGENT-UPGRADE-${user.uid}-${Date.now()}`,
        onClose: () => {
          setLoadingAgentUpgrade(false);
          showError("Agent upgrade payment was cancelled.");
        },
        callback: onPaystackSuccess,
      });

      if (!handler || typeof handler.openIframe !== "function") {
        throw new Error("Unable to open payment window. Please try again.");
      }

      handler.openIframe();
    } catch (paystackError) {
      setLoadingAgentUpgrade(false);
      showError(paystackError?.message || "Unable to start payment. Please try again.");
    }
  }

  async function handleLogout() {
    await signOut(auth);
    navigate("/");
  }

  function openAdminPurchaseRequest() {
    if (!failedPurchaseDraft) {
      return;
    }

    const walletBalance = Number(profile?.walletBalance || 0).toFixed(2);
    const message = [
      "Hello Admin, manual data purchase request.",
      `Agent: ${profile?.fullName || profile?.businessName || user?.email || "Unknown"}`,
      `Agent Email: ${profile?.email || user?.email || "N/A"}`,
      `Network: ${failedPurchaseDraft.network}`,
      `Package: ${failedPurchaseDraft.packageId}`,
      `Beneficiary: ${failedPurchaseDraft.beneficiary}`,
      `Amount: GHS ${Number(failedPurchaseDraft.amount || 0).toFixed(2)}`,
      `Wallet Shown: GHS ${walletBalance}`,
      `Reason: Firestore quota exceeded on agent dashboard.`,
    ].join("\n");

    const waUrl = `https://wa.me/${WHATSAPP_SUPPORT_PHONE}?text=${encodeURIComponent(message)}`;
    window.open(waUrl, "_blank", "noopener,noreferrer");
  }

  const profileName = profile?.fullName || profile?.businessName || user?.email || "Agent";
  const profileEmail = profile?.email || user?.email || "No email";
  const profilePhone = profile?.phone || localStorage.getItem("agent-sim-number") || "Not set";
  const savedSimNumber = localStorage.getItem("agent-sim-number") || profile?.phone || "Not set";
  const totalPurchases = history.filter((item) => item.type === "data" || item.type === "airtime" || !item.type).length;
  const totalTopups = topups.length;
  const profileFormChanged =
    String(profileFormName || "").trim() !== String(profile?.fullName || profile?.businessName || "").trim() ||
    String(profileFormPhone || "").trim() !== String(profile?.phone || "").trim();

  if (authLoading || loadingPage || !profile) {
    return (
      <div className="min-h-screen bg-slate-100 text-slate-900 flex items-center justify-center">
        <div className="rounded-2xl bg-white px-6 py-5 shadow-lg">Loading agent workspace...</div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen pb-24 ${isDark ? "bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" : "bg-gradient-to-br from-slate-50 via-white to-slate-100"}`}>
      <div className="mx-auto flex min-h-screen max-w-6xl flex-col gap-6 px-4 py-5 md:px-6">
        {activeTab === "home" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mx-auto w-full max-w-md space-y-6"
          >
            {/* Enhanced App Header */}
            <div className="flex items-center justify-between">
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="relative"
              >
                <img src={PROFILE_LOGO_SRC} alt="User profile logo" className="h-12 w-12 rounded-2xl object-cover shadow-lg ring-2 ring-white/20" />
                <div className="absolute -bottom-1 -right-1 h-4 w-4 rounded-full bg-gradient-to-r from-emerald-400 to-emerald-500 shadow-sm" />
              </motion.div>

              <div className="flex items-center gap-3">
                <motion.img
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  src={TITLE_LOGO_SRC}
                  alt="UK Connect"
                  className="h-9 w-auto max-w-[160px] object-contain drop-shadow-sm"
                />
              </div>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={openNotifications}
                className={`relative rounded-2xl border p-3 shadow-sm transition-all duration-200 ${
                  isDark
                    ? "border-slate-700/50 bg-slate-800/50 text-slate-300 hover:bg-slate-700/50 backdrop-blur-sm"
                    : "border-white/50 bg-white/80 text-slate-600 hover:bg-white/90 backdrop-blur-sm"
                }`}
              >
                {unreadNotifications > 0 && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -right-1 -top-1 inline-flex min-h-5 min-w-5 items-center justify-center rounded-full bg-gradient-to-r from-red-500 to-red-600 px-1 text-[10px] font-bold text-white shadow-lg ring-2 ring-white"
                  >
                    {unreadNotifications > 9 ? "9+" : unreadNotifications}
                  </motion.span>
                )}
                <Bell size={20} />
              </motion.button>
            </div>

            {/* Enhanced Greeting */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="space-y-2"
            >
              <h1 className={`text-2xl font-bold tracking-tight ${isDark ? "text-white" : "text-slate-900"}`}>
                Welcome back, {profile.fullName?.split(" ")[0] || "Agent"}
              </h1>
              <div className="flex items-center gap-2">
                <span className={`inline-block px-2 py-1 text-xs font-semibold rounded-full ${profile?.paid !== false ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                  {profile?.paid !== false ? 'Agent' : 'Customer'}
                </span>
              </div>
              {profile?.paid === false && (
                <p className={`text-sm font-medium ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                  Go to profile and activate your agent account
                </p>
              )}
            </motion.div>

            {/* Enhanced Wallet Balance Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className={`relative overflow-hidden rounded-3xl border p-6 shadow-xl ${
                isDark
                  ? "border-slate-700/50 bg-gradient-to-br from-slate-800 to-slate-900"
                  : "border-white/50 bg-gradient-to-br from-white to-slate-50/50"
              } backdrop-blur-sm`}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-purple-500/5" />
              <div className="relative flex items-start justify-between">
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className={`rounded-xl p-2 shadow-sm ${
                      isDark ? "bg-slate-700/50" : "bg-slate-100"
                    }`}>
                      <svg className={`h-5 w-5 ${isDark ? "text-slate-300" : "text-slate-600"}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                        <rect x="2" y="5" width="20" height="14" rx="2"/>
                        <path d="M2 10h20"/>
                      </svg>
                    </div>
                    <div>
                      <p className={`text-xs font-semibold uppercase tracking-wide ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                        Wallet Balance
                      </p>
                    </div>
                  </div>
                  <p className={`text-xl font-bold tracking-tight ${isDark ? "text-white" : "text-slate-900"}`}>
                    {balanceHidden ? "GH\u00a2 \u25cf\u25cf\u25cf\u25cf\u25cf" : `GH\u00a2 ${Number(profile.walletBalance || 0).toFixed(2)}`}
                  </p>
                </div>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setBalanceHidden((h) => !h)}
                  className={`rounded-2xl p-3 shadow-sm transition-all duration-200 ${
                    isDark ? "bg-slate-700/50 text-slate-300 hover:bg-slate-600/50" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  {balanceHidden ? <Eye size={24} /> : <EyeOff size={24} />}
                </motion.button>
              </div>
            </motion.div>

            {/* Enhanced Service Tabs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className={`flex rounded-2xl p-1.5 shadow-lg ${
                isDark ? "bg-slate-800/50 backdrop-blur-sm" : "bg-white/80 backdrop-blur-sm"
              }`}
            >
              {[
                { id: "data", label: "Buy Data", icon: "📊" },
                { id: "airtime", label: "Airtime", icon: "📱" },
                { id: "topup", label: "Top Up", icon: "💳" }
              ].map((tab) => (
                <motion.button
                  key={tab.id}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setServiceTab(tab.id)}
                  className={`flex-1 rounded-xl py-3 px-4 text-sm font-bold transition-all duration-200 ${
                    serviceTab === tab.id
                      ? "bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg"
                      : isDark
                        ? "text-slate-300 hover:bg-slate-700/50"
                        : "text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  <div className="flex flex-col items-center gap-1">
                    <span className="text-lg">{tab.icon}</span>
                    <span className="text-xs font-semibold">{tab.label}</span>
                  </div>
                </motion.button>
              ))}
            </motion.div>

            {/* Enhanced Buy Data Form */}
            {serviceTab === "data" && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className={`relative overflow-hidden rounded-3xl border p-6 shadow-xl space-y-6 ${
                  isDark
                    ? "border-slate-700/50 bg-gradient-to-br from-slate-800 to-slate-900"
                    : "border-white/50 bg-gradient-to-br from-white to-slate-50/50"
                } backdrop-blur-sm`}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-purple-500/5" />

                {/* Network Selection */}
                <div className="relative space-y-4">
                  <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">
                    Select Network
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {NETWORKS.map((network) => (
                      <motion.div
                        key={network.name}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="space-y-2"
                      >
                        <button
                          onClick={() => setSelectedNetwork(network.name)}
                          className={`group relative w-full overflow-hidden rounded-2xl border-2 p-4 transition-all duration-300 ${
                            selectedNetwork === network.name
                              ? "border-blue-500 bg-gradient-to-br from-blue-50 to-blue-100 shadow-lg dark:from-blue-900/20 dark:to-blue-800/20"
                              : isDark
                                ? "border-slate-600/50 bg-slate-700/30 hover:border-slate-500/50 hover:bg-slate-600/30"
                                : "border-slate-200 bg-white/80 hover:border-slate-300 hover:bg-slate-50/80"
                          }`}
                        >
                          <div className="flex flex-col items-center gap-2">
                            <img
                              src={network.logo}
                              alt={network.name}
                              className="h-8 w-8 object-contain transition-transform group-hover:scale-110"
                            />
                            <span className={`text-sm font-bold ${
                              selectedNetwork === network.name
                                ? "text-blue-700 dark:text-blue-300"
                                : isDark ? "text-slate-300" : "text-slate-700"
                            }`}>
                              {network.shortLabel}
                            </span>
                          </div>
                          {selectedNetwork === network.name && (
                            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-transparent" />
                          )}
                        </button>

                        {(() => {
                          const rawStatus = String(networkStatus?.[network.name]?.status || "good").toLowerCase();
                          const labelStatus = rawStatus === "down" ? "bad" : rawStatus;
                          const statusConfig = {
                            good: { color: "text-emerald-600", bg: "bg-emerald-100 dark:bg-emerald-900/20", label: "Excellent" },
                            slow: { color: "text-amber-600", bg: "bg-amber-100 dark:bg-amber-900/20", label: "Slow" },
                            bad: { color: "text-red-600", bg: "bg-red-100 dark:bg-red-900/20", label: "Issues" }
                          };
                          const config = statusConfig[labelStatus] || statusConfig.good;

                          return (
                            <div className={`flex items-center justify-center gap-1.5 rounded-lg px-2 py-1 text-[10px] font-bold ${config.color} ${config.bg}`}>
                              <NetworkStatusBadge status={labelStatus} />
                              <span>{config.label}</span>
                            </div>
                          );
                        })()}
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Package Selection */}
                <div className="space-y-3">
                  <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">
                    Choose Package
                  </label>
                  <div className="relative">
                    <motion.button
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                      type="button"
                      onClick={() => setShowPackagePicker(true)}
                      className={`group relative w-full overflow-hidden rounded-2xl border-2 px-5 py-4 text-left text-base font-bold outline-none transition-all duration-200 ${
                        isDark
                          ? "border-slate-600 bg-slate-800/50 text-slate-100 hover:border-slate-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                          : "border-slate-300 bg-white text-slate-900 hover:border-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className={selectedPackage ? "text-slate-900 dark:text-slate-100" : "text-slate-500"}>
                          {selectedPackage ? `${selectedPackage.id} - ${formatGhs(selectedPackage.promoPrice)}` : "Select a package"}
                        </span>
                        <ChevronDown className="h-5 w-5 text-slate-400 transition-transform group-hover:rotate-180" />
                      </div>
                    </motion.button>
                  </div>
                  {selectedPackage && effectivePromoActive && selectedPackage.promoSavings > 0 && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="rounded-xl bg-gradient-to-r from-emerald-50 to-emerald-100 p-3 dark:from-emerald-900/20 dark:to-emerald-800/20"
                    >
                      <p className="text-sm font-bold text-emerald-700 dark:text-emerald-300">
                        🎉 Promo Active! Save {formatGhs(selectedPackage.promoSavings)}
                      </p>
                    </motion.div>
                  )}
                </div>

                {/* Beneficiary Phone Number */}
                <div className="space-y-3">
                  <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">
                    Beneficiary Phone Number
                  </label>
                  <div className="relative">
                    <input
                      value={beneficiary}
                      onChange={(event) => setBeneficiary(event.target.value)}
                      placeholder="024xxxxxxx"
                      maxLength={10}
                      className={`w-full rounded-2xl border-2 px-5 py-4 text-base font-semibold outline-none transition-all duration-200 ${
                        isDark
                          ? "border-slate-600 bg-slate-800/50 text-slate-100 placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                          : "border-slate-300 bg-white text-slate-900 placeholder:text-slate-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                      }`}
                    />
                    <div className="absolute right-4 top-1/2 -translate-y-1/2">
                      <Smartphone className="h-5 w-5 text-slate-400" />
                    </div>
                  </div>
                </div>

                {/* Enhanced Buy Data Button */}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handlePurchase}
                  disabled={loadingPurchase || !packagesActive || !beneficiary || !selectedPackageId}
                  className={`group relative w-full overflow-hidden rounded-2xl px-6 py-5 text-lg font-bold transition-all duration-300 ${
                    !loadingPurchase && packagesActive && beneficiary && selectedPackageId
                      ? "bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg hover:shadow-xl hover:from-blue-700 hover:to-blue-800"
                      : "bg-slate-200 text-slate-500 cursor-not-allowed dark:bg-slate-700 dark:text-slate-400"
                  }`}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="relative flex items-center justify-center gap-3">
                    {loadingPurchase ? (
                      <>
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        >
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                          </svg>
                        </motion.div>
                        <span>Processing Purchase...</span>
                      </>
                    ) : (
                      <>
                        <Package className="h-5 w-5" />
                        <span>Buy Data Bundle</span>
                      </>
                    )}
                  </div>
                </motion.button>

                {failedPurchaseDraft && (
                  <motion.button
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={openAdminPurchaseRequest}
                    className="w-full rounded-2xl border-2 border-emerald-300 bg-gradient-to-r from-emerald-50 to-emerald-100 px-6 py-4 text-sm font-bold text-emerald-700 transition-all hover:from-emerald-100 hover:to-emerald-200 dark:from-emerald-900/20 dark:to-emerald-800/20 dark:border-emerald-600 dark:text-emerald-300"
                  >
                    <div className="flex items-center justify-center gap-2">
                      <MessageSquare className="h-4 w-4" />
                      <span>Request Manual Purchase</span>
                    </div>
                  </motion.button>
                )}
              </motion.div>
            )}

            {serviceTab === "airtime" && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className={`relative overflow-hidden rounded-3xl border p-6 shadow-xl space-y-6 ${
                  isDark
                    ? "border-slate-700/50 bg-gradient-to-br from-slate-800 to-slate-900"
                    : "border-white/50 bg-gradient-to-br from-white to-slate-50/50"
                } backdrop-blur-sm`}
              >
                <div className="absolute inset-0 pointer-events-none bg-gradient-to-br from-blue-500/5 via-transparent to-purple-500/5" />
                
                <div className="relative space-y-6">
                  {/* Network Selection */}
                  <div className="space-y-3">
                    <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">
                      Select Network
                    </label>
                    <div className="grid grid-cols-3 gap-3">
                      {NETWORKS.map((net) => (
                        <motion.button
                          key={net.name}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setAirtimeNetwork(net.name)}
                          className={`relative overflow-hidden rounded-2xl border-2 transition-all duration-300 p-3 flex flex-col items-center gap-2 ${
                            airtimeNetwork === net.name
                              ? "border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-900/30"
                              : isDark
                              ? "border-slate-600 bg-slate-700/30 hover:border-slate-500"
                              : "border-slate-300 bg-slate-100/50 hover:border-slate-400"
                          }`}
                        >
                          <img
                            src={net.logo}
                            alt={net.name}
                            className="h-6 w-6 object-contain"
                          />
                          <span className={`text-xs font-bold ${
                            airtimeNetwork === net.name
                              ? "text-blue-600 dark:text-blue-300"
                              : isDark
                              ? "text-slate-400"
                              : "text-slate-600"
                          }`}>
                            {net.shortLabel}
                          </span>
                        </motion.button>
                      ))}
                    </div>
                  </div>

                  {/* Request Number */}
                  <div className="space-y-3">
                    <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">
                      Airtime Request Number
                    </label>
                    <input
                      type="text"
                      value={airtimeRequestNumber}
                      onChange={(e) => setAirtimeRequestNumber(e.target.value)}
                      placeholder="Enter request number"
                      className={`w-full rounded-2xl border-2 px-4 py-3 font-semibold transition-all duration-200 outline-none ${
                        isDark
                          ? "border-slate-600 bg-slate-800/50 text-slate-100 placeholder:text-slate-500 focus:border-blue-500 focus:bg-slate-800/75"
                          : "border-slate-300 bg-white text-slate-900 placeholder:text-slate-500 focus:border-blue-500 focus:bg-slate-50"
                      }`}
                    />
                  </div>

                  {/* Amount */}
                  <div className="space-y-3">
                    <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">
                      Amount (GHS)
                    </label>
                    <div className={`relative rounded-2xl border-2 transition-all duration-200 ${
                      isDark
                        ? "border-slate-600 bg-slate-800/50 focus-within:border-blue-500"
                        : "border-slate-300 bg-white focus-within:border-blue-500"
                    }`}>
                      <div className="flex items-center">
                        <div className="flex items-center justify-center px-4 py-4">
                          <span className={`text-lg font-bold ${isDark ? "text-slate-300" : "text-slate-500"}`}>GH₵</span>
                        </div>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={airtimeAmount === "" ? "" : airtimeAmount}
                          onChange={(e) => setAirtimeAmount(e.target.value)}
                          placeholder="0.00"
                          className={`flex-1 bg-transparent px-2 py-4 text-lg font-bold outline-none ${
                            isDark ? "text-slate-100 placeholder:text-slate-400" : "text-slate-900 placeholder:text-slate-500"
                          }`}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Purchase Button */}
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleAirtimePurchase}
                    disabled={loadingAirtimePurchase || !airtimeRequestNumber.trim() || !airtimeAmount || Number(airtimeAmount) <= 0}
                    className={`group relative w-full overflow-hidden rounded-2xl px-6 py-5 text-lg font-bold transition-all duration-300 ${
                      !loadingAirtimePurchase && airtimeRequestNumber.trim() && airtimeAmount && Number(airtimeAmount) > 0
                        ? "bg-gradient-to-r from-purple-600 to-purple-700 text-white shadow-lg hover:shadow-xl hover:from-purple-700 hover:to-purple-800"
                        : "bg-slate-200 text-slate-500 cursor-not-allowed dark:bg-slate-700 dark:text-slate-400"
                    }`}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="relative flex items-center justify-center gap-3">
                      {loadingAirtimePurchase ? (
                        <>
                          <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                          >
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                            </svg>
                          </motion.div>
                          <span>Processing Airtime...</span>
                        </>
                      ) : (
                        <>
                          <Smartphone className="h-5 w-5" />
                          <span>Buy Airtime</span>
                        </>
                      )}
                    </div>
                  </motion.button>
                </div>
              </motion.div>
            )}

            {/* Enhanced Top Up Form */}
            {serviceTab === "topup" && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className={`relative overflow-hidden rounded-3xl border p-6 shadow-xl space-y-6 ${
                  isDark
                    ? "border-slate-700/50 bg-gradient-to-br from-slate-800 to-slate-900"
                    : "border-white/50 bg-gradient-to-br from-white to-slate-50/50"
                } backdrop-blur-sm`}
              >
                <div className="absolute inset-0 pointer-events-none bg-gradient-to-br from-green-500/5 via-transparent to-blue-500/5" />

                {/* Amount Input with Enhanced Controls */}
                <div className="relative space-y-3">
                  <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">
                    Top-up Amount (GHS)
                  </label>
                  <div className={`relative rounded-2xl border-2 transition-all duration-200 ${
                    isDark
                      ? "border-slate-600 bg-slate-800/50 focus-within:border-green-500"
                      : "border-slate-300 bg-white focus-within:border-green-500"
                  }`}>
                    <div className="flex items-center">
                      <div className="flex items-center justify-center px-4 py-4">
                        <span className={`text-lg font-bold ${isDark ? "text-slate-300" : "text-slate-500"}`}>GH₵</span>
                      </div>
                      <input
                        type="number"
                        min="0"
                        value={topupAmount === 0 ? "" : topupAmount}
                        onChange={(e) => setTopupAmount(Math.max(0, Number(e.target.value) || 0))}
                        placeholder="0.00"
                        className={`flex-1 bg-transparent px-2 py-4 text-lg font-bold outline-none ${
                          isDark ? "text-slate-100 placeholder:text-slate-400" : "text-slate-900 placeholder:text-slate-500"
                        }`}
                      />
                      <div className="flex flex-col border-l border-slate-200 dark:border-slate-600">
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => setTopupAmount(prev => Math.max(0, prev + 10))}
                          className={`px-3 py-1 transition-colors ${
                            isDark ? "text-slate-400 hover:text-green-400" : "text-slate-500 hover:text-green-600"
                          } hover:bg-green-50 dark:hover:bg-green-900/20`}
                        >
                          <ChevronDown className="h-4 w-4 rotate-180" />
                        </motion.button>
                        <div className="h-px bg-slate-200 dark:bg-slate-600" />
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => setTopupAmount(prev => Math.max(0, prev - 10))}
                          className={`px-3 py-1 transition-colors ${
                            isDark ? "text-slate-400 hover:text-red-400" : "text-slate-500 hover:text-red-600"
                          } hover:bg-red-50 dark:hover:bg-red-900/20`}
                        >
                          <ChevronDown className="h-4 w-4" />
                        </motion.button>
                      </div>
                    </div>
                  </div>

                  {/* Quick Amount Buttons */}
                  <div className="flex gap-2">
                    {[50, 100, 200, 500].map((amount) => (
                      <motion.button
                        key={amount}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setTopupAmount(amount)}
                        className={`rounded-xl px-4 py-2 text-sm font-bold transition-all ${
                          topupAmount === amount
                            ? "bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg"
                            : isDark
                              ? "bg-slate-700/50 text-slate-300 hover:bg-slate-600/50"
                              : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        }`}
                      >
                        GH₵ {amount}
                      </motion.button>
                    ))}
                  </div>
                </div>

                {/* Payment Method Selection */}
                <div className="space-y-3">
                  <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">
                    Payment Method
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setTopupMethod("paystack")}
                      className={`relative overflow-hidden rounded-2xl border-2 px-4 py-4 text-sm font-bold transition-all ${
                        topupMethod === "paystack"
                          ? "border-blue-500 bg-gradient-to-r from-blue-50 to-blue-100 text-blue-700 shadow-lg dark:from-blue-900/20 dark:to-blue-800/20 dark:text-blue-300"
                          : isDark
                            ? "border-slate-600 bg-slate-800/50 text-slate-300 hover:border-slate-500"
                            : "border-slate-300 bg-white text-slate-600 hover:border-slate-400"
                      }`}
                    >
                      <div className="flex flex-col items-center gap-2">
                        <CreditCard size={20} />
                        <span>Paystack</span>
                        <span className="text-xs opacity-75">Instant</span>
                      </div>
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setTopupMethod("manual")}
                      className={`relative overflow-hidden rounded-2xl border-2 px-4 py-4 text-sm font-bold transition-all ${
                        topupMethod === "manual"
                          ? "border-amber-500 bg-gradient-to-r from-amber-50 to-amber-100 text-amber-700 shadow-lg dark:from-amber-900/20 dark:to-amber-800/20 dark:text-amber-300"
                          : isDark
                            ? "border-slate-600 bg-slate-800/50 text-slate-300 hover:border-slate-500"
                            : "border-slate-300 bg-white text-slate-600 hover:border-slate-400"
                      }`}
                    >
                      <div className="flex flex-col items-center gap-2">
                        <UserCheck size={20} />
                        <span>Manual</span>
                        <span className="text-xs opacity-75">Admin Approved</span>
                      </div>
                    </motion.button>
                  </div>
                </div>

                {/* Manual Payment Details */}
                {topupMethod === "manual" && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className={`rounded-2xl border p-4 space-y-4 ${
                      isDark
                        ? "border-amber-500/30 bg-gradient-to-r from-amber-900/10 to-amber-800/10"
                        : "border-amber-200 bg-gradient-to-r from-amber-50 to-amber-100/50"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <AlertTriangle size={16} className="text-amber-600" />
                      <span className={`text-sm font-bold ${isDark ? "text-amber-300" : "text-amber-700"}`}>Direct Deposit Details</span>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className={`text-sm font-semibold ${isDark ? "text-slate-300" : "text-slate-700"}`}>MOMO Number:</span>
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-bold ${isDark ? "text-slate-100" : "text-slate-900"}`}>{manualPaymentNumber}</span>
                          <motion.button
                            type="button"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => { navigator.clipboard.writeText(manualPaymentNumber); setCopiedField("number"); setTimeout(() => setCopiedField(null), 2000); }}
                            className={`rounded-lg p-2 transition-colors ${isDark ? "bg-amber-900/20 text-amber-300 hover:bg-amber-800/30" : "bg-amber-100 text-amber-700 hover:bg-amber-200"}`}
                          >
                            {copiedField === "number" ? <ClipboardCheck size={14} /> : <Copy size={14} />}
                          </motion.button>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <span className={`text-sm font-semibold ${isDark ? "text-slate-300" : "text-slate-700"}`}>Account Name:</span>
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-bold ${isDark ? "text-slate-100" : "text-slate-900"}`}>{manualPaymentName}</span>
                          <motion.button
                            type="button"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => { navigator.clipboard.writeText(manualPaymentName); setCopiedField("name"); setTimeout(() => setCopiedField(null), 2000); }}
                            className={`rounded-lg p-2 transition-colors ${isDark ? "bg-amber-900/20 text-amber-300 hover:bg-amber-800/30" : "bg-amber-100 text-amber-700 hover:bg-amber-200"}`}
                          >
                            {copiedField === "name" ? <ClipboardCheck size={14} /> : <Copy size={14} />}
                          </motion.button>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <span className={`text-sm font-semibold ${isDark ? "text-slate-300" : "text-slate-700"}`}>Payment Reference:</span>
                        <div className="flex items-center gap-2">
                          <div className={`flex-1 rounded-lg px-3 py-2 font-mono text-sm font-bold ${
                            isDark ? "bg-slate-800 text-slate-100" : "bg-slate-100 text-slate-900"
                          }`}>
                            {manualReference || (
                              <span className={`italic ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                                Click Generate to create reference
                              </span>
                            )}
                          </div>
                          <motion.button
                            type="button"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={generateManualReference}
                            className="rounded-lg bg-gradient-to-r from-blue-500 to-blue-600 px-4 py-2 text-sm font-bold text-white shadow-lg hover:from-blue-600 hover:to-blue-700"
                          >
                            Generate
                          </motion.button>
                          {manualReference && (
                            <motion.button
                              type="button"
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => { navigator.clipboard.writeText(manualReference); setCopiedField("ref"); setTimeout(() => setCopiedField(null), 2000); }}
                              className={`rounded-lg p-2 transition-colors ${isDark ? "bg-slate-700 text-slate-300 hover:bg-slate-600" : "bg-slate-200 text-slate-700 hover:bg-slate-300"}`}
                            >
                              {copiedField === "ref" ? <ClipboardCheck size={14} /> : <Copy size={14} />}
                            </motion.button>
                          )}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className={`block text-sm font-semibold ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                          Reason (Optional)
                        </label>
                        <textarea
                          value={topupManualReason}
                          onChange={(e) => setTopupManualReason(e.target.value)}
                          placeholder="Provide context for your top-up request..."
                          rows={3}
                          className={`w-full rounded-xl border px-4 py-3 text-sm outline-none transition-all ${
                            isDark
                              ? "border-slate-600 bg-slate-800/50 text-slate-100 placeholder:text-slate-400 focus:border-blue-500"
                              : "border-slate-300 bg-white text-slate-900 placeholder:text-slate-500 focus:border-blue-500"
                          }`}
                        />
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Top Up Action Button */}
                <motion.button
                  whileHover={{ scale: topupAmount >= 20 && !loadingTopup ? 1.02 : 1 }}
                  whileTap={{ scale: topupAmount >= 20 && !loadingTopup ? 0.98 : 1 }}
                  onClick={topupMethod === "paystack" ? handleTopupWithPaystack : handleManualPaymentConfirmation}
                  disabled={loadingTopup || topupAmount < 20 || (topupMethod === "manual" && !String(manualReference || "").trim())}
                  className={`relative w-full overflow-hidden rounded-2xl px-6 py-4 text-lg font-bold transition-all ${
                    !loadingTopup && topupAmount >= 20 && (topupMethod === "paystack" || String(manualReference || "").trim())
                      ? "bg-gradient-to-r from-green-500 to-green-600 text-white shadow-xl hover:from-green-600 hover:to-green-700"
                      : "bg-slate-200 text-slate-500 dark:bg-slate-700 dark:text-slate-400"
                  }`}
                >
                  {loadingTopup ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      Processing...
                    </div>
                  ) : topupMethod === "paystack" ? (
                    <div className="flex items-center justify-center gap-2">
                      <CreditCard size={20} />
                      Top Up Wallet
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2">
                      <Send size={20} />
                      I Have Paid
                    </div>
                  )}
                </motion.button>

                {topupMethod === "manual" && (
                  <p className={`text-center text-xs leading-relaxed ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                    Generate a reference first, then tap "I Have Paid" to alert admin.
                  </p>
                )}
              </motion.div>
            )}
          </motion.div>
        )}

        {activeTab === "purchases" && (
          <div className="mx-auto w-full max-w-md space-y-4">
            {/* Header */}
            <div>
              <h2 className={`text-2xl font-black ${isDark ? "text-white" : "text-slate-900"}`}>Purchases</h2>
            </div>

            {/* Tabs */}
            <div className="flex gap-2">
              <button
                onClick={() => setPurchaseTab("data")}
                className={`flex-1 rounded-2xl px-4 py-3 text-sm font-bold transition-all ${
                  purchaseTab === "data"
                    ? "bg-[#274193] text-white"
                    : isDark
                    ? "bg-slate-800 text-white"
                    : "bg-slate-100 text-slate-600"
                }`}
              >
                Data
              </button>
              <button
                onClick={() => setPurchaseTab("airtime")}
                className={`flex-1 rounded-2xl px-4 py-3 text-sm font-bold transition-all ${
                  purchaseTab === "airtime"
                    ? "bg-[#274193] text-white"
                    : isDark
                    ? "bg-slate-800 text-white"
                    : "bg-slate-100 text-slate-600"
                }`}
              >
                Airtime
              </button>
              <button
                onClick={() => setPurchaseTab("topups")}
                className={`flex-1 rounded-2xl px-4 py-3 text-sm font-bold transition-all ${
                  purchaseTab === "topups"
                    ? "bg-[#274193] text-white"
                    : isDark
                    ? "bg-slate-800 text-white"
                    : "bg-slate-100 text-slate-600"
                }`}
              >
                Top-Ups
              </button>
            </div>

            {/* Purchase Cards */}
            {(purchaseTab === "data" || purchaseTab === "airtime") && (
              <div className="space-y-3">
                {history.length === 0 ? (
                  <div
                    className={`rounded-2xl border p-5 text-center ${
                      isDark ? "border-white/10 bg-slate-900" : "border-slate-300 bg-slate-50"
                    }`}
                  >
                    <p className={isDark ? "text-white/60" : "text-slate-500"}>No {purchaseTab} purchases yet.</p>
                  </div>
                ) : (
                  history
                    .filter((item) => item.type === purchaseTab || !item.type)
                    .map((item) => (
                      <motion.div
                        key={item.id}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`rounded-2xl border p-4 ${isDark ? "border-white/10 bg-slate-900" : "border-slate-300 bg-white"}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-xl">{item.type === "airtime" ? "📱" : "😊"}</span>
                              <div>
                                <p className={`text-sm font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
                                  {item.type === "airtime" 
                                    ? `${item.network} Airtime` 
                                    : item.packageId}
                                </p>
                                <p className={`text-xs ${isDark ? "text-white/60" : "text-slate-500"}`}>
                                  {item.type === "airtime" 
                                    ? `Ref: ${item.requestNumber || item.assignedTo}` 
                                    : item.assignedTo}
                                </p>
                              </div>
                            </div>
                            <p className={`mt-1 text-xs ${isDark ? "text-white/50" : "text-slate-400"}`}>
                              {item.assignedAt?.toDate ? item.assignedAt.toDate().toLocaleDateString() : "Pending"}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-black">{formatGhs(item.totalPaid)}</p>
                            <StatusBadge status={item.status} />
                          </div>
                          <ChevronDown size={20} className={isDark ? "text-white/40" : "text-slate-400"} />
                        </div>
                      </motion.div>
                    ))
                )}
              </div>
            )}

            {/* Top-Ups Cards */}
            {purchaseTab === "topups" && (
              <div className="space-y-3">
                {topups.length === 0 ? (
                  <div
                    className={`rounded-2xl border p-5 text-center ${
                      isDark ? "border-white/10 bg-slate-900" : "border-slate-300 bg-slate-50"
                    }`}
                  >
                    <p className={isDark ? "text-white/60" : "text-slate-500"}>No top-ups yet.</p>
                  </div>
                ) : (
                  topups.map((item) => (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`rounded-2xl border p-4 ${isDark ? "border-white/10 bg-slate-900" : "border-slate-300 bg-white"}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xl">➕</span>
                            <div>
                              <p className={`text-sm font-bold ${isDark ? "text-white" : "text-slate-900"}`}>{item.title || "Wallet Top-Up"}</p>
                              <p className={`text-xs ${isDark ? "text-white/60" : "text-slate-500"}`}>
                                {item.methodLabel || (item.method === "paystack" ? "Paystack Payment" : "Admin Credit")}
                              </p>
                              {item.paymentReference && (
                                <p className={`mt-1 text-[11px] ${isDark ? "text-white/50" : "text-slate-400"}`}>
                                  Ref: {item.paymentReference}
                                </p>
                              )}
                            </div>
                          </div>
                          <p className={`mt-1 text-xs ${isDark ? "text-white/50" : "text-slate-400"}`}>
                            {item.createdAt?.toDate ? item.createdAt.toDate().toLocaleDateString() : "Pending"}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className={`text-sm font-black ${["approved", "completed"].includes(String(item.status || "").toLowerCase()) ? "text-emerald-500" : String(item.status || "").toLowerCase() === "rejected" ? "text-red-500" : "text-amber-500"}`}>
                            {["approved", "completed"].includes(String(item.status || "").toLowerCase()) ? "+" : ""}{formatGhs(item.amount)}
                          </p>
                          <StatusBadge status={item.status} />
                        </div>
                        <ChevronDown size={20} className={isDark ? "text-white/40" : "text-slate-400"} />
                      </div>
                    </motion.div>
                  ))
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === "support" && (
          <div className="mx-auto w-full max-w-md space-y-4">
            <div className="text-center">
              <p className={`text-3xl font-extrabold ${isDark ? "text-white" : "text-slate-900"}`}>How can we help?</p>
              <p className={`mt-1 text-sm ${isDark ? "text-slate-300" : "text-slate-500"}`}>Get in touch with our team via WhatsApp</p>
            </div>

            <div className="relative overflow-hidden rounded-3xl bg-[#20ba56] p-5 text-white shadow-[0_10px_24px_rgba(16,185,129,0.28)]">
              <div className="absolute -right-10 -top-8 h-28 w-28 rounded-full bg-white/10" />
              <div className="absolute -right-4 bottom-[-32px] h-28 w-28 rounded-full bg-white/10" />

              <div className="relative flex gap-3">
                <div className="mt-1 rounded-xl bg-white/20 p-2.5">
                  <MessageSquare size={18} />
                </div>

                <div>
                  <p className="text-2xl font-extrabold">Contact Admin</p>
                  <p className="mt-1 max-w-[260px] text-sm text-emerald-50">
                    Chat directly with our admin team for personalized assistance
                  </p>
                  <p className="mt-2 text-sm text-emerald-50">
                    Support: <span className="font-bold">{SUPPORT_NUMBER}</span>
                  </p>

                  <a
                    className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-white"
                    href={`https://wa.me/${WHATSAPP_SUPPORT_PHONE}?text=Hello%20I%20need%20help%20with%20my%20order`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Start Chat
                    <ExternalLink size={14} />
                  </a>
                </div>
              </div>
            </div>

            <div className={`rounded-3xl border border-[#20ba56] p-4 ${isDark ? "bg-slate-900/50" : "bg-[#f0fff4]"}`}>
              <div className="flex items-start gap-3">
                <div className="rounded-xl bg-[#20ba56] p-2.5 text-white">
                  <MessageSquare size={18} />
                </div>

                <div>
                  <p className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-slate-900"}`}>Join Our Group</p>
                  <p className={`mt-1 text-sm leading-relaxed ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                    Be part of our WhatsApp community for updates, tips, and exclusive offers.
                  </p>

                  <a
                    className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-[#20ba56] px-3 py-2 text-sm font-bold text-white hover:bg-[#1a9648] transition-colors"
                    href={WHATSAPP_GROUP_LINK}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Join WhatsApp Group
                    <ExternalLink size={14} />
                  </a>
                </div>
              </div>
            </div>

            <div className={`rounded-3xl border p-4 ${isDark ? "border-slate-700 bg-slate-900" : "border-[#d7d8de] bg-[#f6f6f8]"}`}>
              <div className="flex items-start gap-3">
                <div className={`rounded-xl p-2.5 ${isDark ? "bg-slate-800 text-slate-300" : "bg-[#e8e9ed] text-slate-500"}`}>
                  <Clock3 size={18} />
                </div>

                <div className={isDark ? "text-slate-300" : "text-slate-600"}>
                  <p className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-slate-900"}`}>Support Hours</p>
                  <p className="mt-1 text-sm leading-relaxed">
                    Our support team is available <span className={`font-bold ${isDark ? "text-white" : "text-slate-900"}`}>Monday to Sunday, 7:00 AM - 10:00 PM (GMT)</span>. We aim to respond to all messages within 24 hours.
                  </p>
                </div>
              </div>
            </div>

            <div className={`rounded-3xl border p-4 ${isDark ? "border-slate-700 bg-slate-900" : "border-[#d7d8de] bg-[#f6f6f8]"}`}>
              <div className="flex items-start gap-3">
                <div className={`rounded-xl p-2.5 ${isDark ? "bg-slate-800 text-slate-300" : "bg-[#e8e9ed] text-slate-500"}`}>
                  <Code2 size={18} />
                </div>

                <div className={isDark ? "text-slate-300" : "text-slate-600"}>
                  <p className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-slate-900"}`}>About Us</p>
                  <p className="mt-1 text-sm leading-relaxed">
                    UK Connectivity helps you buy data quickly with trusted support and fast delivery updates.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "profile" && (
          <div className="mx-auto w-full max-w-md space-y-5">
            <div className={`overflow-hidden rounded-3xl border border-transparent p-5 shadow-2xl ${isDark ? "bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950 text-white" : "bg-gradient-to-br from-sky-500 via-blue-500 to-indigo-600 text-white"}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                  <img src={PROFILE_LOGO_SRC} alt="Agent logo" className="h-12 w-12 rounded-2xl border border-white/20 bg-white/10 object-cover shadow-lg" />
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/75">User Profile</p>
                    <p className="mt-2 text-2xl font-black leading-tight">{profileName}</p>
                    <p className="mt-1 text-sm text-white/80">{profileEmail}</p>
                  </div>
                </div>
                <span className="rounded-full border border-white/20 bg-white/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.2em] text-white">
                  Active
                </span>
              </div>

              <div className="mt-5 grid grid-cols-2 gap-3 text-xs">
                <div className="rounded-3xl bg-white/10 p-4">
                  <p className="font-semibold text-white/80">Phone</p>
                  <p className="mt-1 text-base font-bold text-white">{profilePhone}</p>
                </div>
                <div className="rounded-3xl bg-white/10 p-4">
                  <p className="font-semibold text-white/80">Wallet</p>
                  <p className="mt-1 text-base font-bold text-white">{formatGhs(profile?.walletBalance || 0)}</p>
                </div>
              </div>

              <div className="mt-5 rounded-3xl bg-white/10 p-4">
                <div className="flex items-center gap-3">
                  <div className="rounded-2xl bg-white/15 p-2 text-white">
                    <UserCheck size={16} />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-white">Become an Agent</p>
                    <p className="mt-1 text-xs text-white/80">Upgrade to agent status for GHS 30. Get access to agent pricing and features.</p>
                  </div>
                </div>
                <button
                  onClick={handleAgentUpgrade}
                  disabled={loadingAgentUpgrade}
                  className={`mt-4 w-full rounded-2xl px-3 py-2 text-xs font-bold transition-all ${
                    loadingAgentUpgrade
                      ? "bg-white/20 text-white"
                      : "bg-white text-blue-700 hover:bg-slate-100"
                  }`}
                >
                  {loadingAgentUpgrade ? "Processing..." : "Pay GHS 30"}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className={`rounded-3xl border p-3 ${isDark ? "border-white/10 bg-slate-900" : "border-slate-200 bg-white"}`}>
                <p className={`text-[11px] font-semibold uppercase tracking-wide ${isDark ? "text-slate-400" : "text-slate-500"}`}>Purchases</p>
                <p className={`mt-2 text-xl font-black ${isDark ? "text-white" : "text-slate-900"}`}>{totalPurchases}</p>
              </div>
              <div className={`rounded-3xl border p-3 ${isDark ? "border-white/10 bg-slate-900" : "border-slate-200 bg-white"}`}>
                <p className={`text-[11px] font-semibold uppercase tracking-wide ${isDark ? "text-slate-400" : "text-slate-500"}`}>Topups</p>
                <p className={`mt-2 text-xl font-black ${isDark ? "text-white" : "text-slate-900"}`}>{totalTopups}</p>
              </div>
              <div className={`rounded-3xl border p-3 ${isDark ? "border-white/10 bg-slate-900" : "border-slate-200 bg-white"}`}>
                <p className={`text-[11px] font-semibold uppercase tracking-wide ${isDark ? "text-slate-400" : "text-slate-500"}`}>Theme</p>
                <p className={`mt-2 text-xl font-black capitalize ${isDark ? "text-white" : "text-slate-900"}`}>{theme}</p>
              </div>
            </div>

            {showProfileExtra ? (
              <>
                <div className="grid gap-3 sm:grid-cols-2">
                  <button
                    onClick={() => setTheme(isDark ? "light" : "dark")}
                    className={`rounded-3xl border p-4 text-left transition ${isDark ? "border-white/10 bg-slate-900 hover:bg-white/5" : "border-slate-200 bg-white hover:bg-slate-50"}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className={`rounded-2xl p-2.5 ${isDark ? "bg-slate-800 text-slate-200" : "bg-slate-100 text-slate-600"}`}>
                        <Moon size={18} />
                      </div>
                      <ChevronRight size={18} className={isDark ? "text-slate-500" : "text-slate-400"} />
                    </div>
                    <p className={`mt-3 text-sm font-bold ${isDark ? "text-white" : "text-slate-900"}`}>Dark Mode</p>
                    <p className={`mt-1 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                      {isDark ? "Switch back to light appearance." : "Enable dark appearance for low-light use."}
                    </p>
                  </button>

                  <button
                    onClick={handleSimSetup}
                    className={`rounded-3xl border p-4 text-left transition ${isDark ? "border-white/10 bg-slate-900 hover:bg-white/5" : "border-slate-200 bg-white hover:bg-slate-50"}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className={`rounded-2xl p-2.5 ${isDark ? "bg-slate-800 text-slate-200" : "bg-slate-100 text-slate-600"}`}>
                        <Smartphone size={18} />
                      </div>
                      <ChevronRight size={18} className={isDark ? "text-slate-500" : "text-slate-400"} />
                    </div>
                    <p className={`mt-3 text-sm font-bold ${isDark ? "text-white" : "text-slate-900"}`}>SIM Setup</p>
                    <p className={`mt-1 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>Default SIM: {savedSimNumber}</p>
                  </button>
                </div>

                <div className={`rounded-3xl border p-4 ${isDark ? "border-white/10 bg-slate-900" : "border-slate-200 bg-white"}`}>
                  <p className={`text-base font-extrabold ${isDark ? "text-white" : "text-slate-900"}`}>Edit Details</p>
                  <p className={`mt-1 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                    Update your name and phone number.
                  </p>

                  <div className="mt-3 space-y-3">
                    <div>
                      <label className={`mb-1.5 block text-xs font-bold uppercase tracking-wide ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                        Full Name
                      </label>
                      <input
                        value={profileFormName}
                        onChange={(event) => setProfileFormName(event.target.value)}
                        placeholder="Enter full name"
                        className={`w-full rounded-2xl border px-4 py-3 text-sm font-semibold outline-none transition-all ${isDark ? "border-slate-600 bg-slate-800 text-white focus:border-[#274193] focus:ring-2 focus:ring-[#274193]/20" : "border-slate-300 bg-white text-slate-900 focus:border-[#274193] focus:ring-2 focus:ring-[#274193]/20"}`}
                      />
                    </div>

                    <div>
                      <label className={`mb-1.5 block text-xs font-bold uppercase tracking-wide ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                        Phone Number
                      </label>
                      <input
                        value={profileFormPhone}
                        onChange={(event) => setProfileFormPhone(event.target.value)}
                        placeholder="024xxxxxxx"
                        maxLength={10}
                        className={`w-full rounded-2xl border px-4 py-3 text-sm font-semibold outline-none transition-all ${isDark ? "border-slate-600 bg-slate-800 text-white focus:border-[#274193] focus:ring-2 focus:ring-[#274193]/20" : "border-slate-300 bg-white text-slate-900 focus:border-[#274193] focus:ring-2 focus:ring-[#274193]/20"}`}
                      />
                    </div>

                    <button
                      onClick={handleProfileUpdate}
                      disabled={savingProfile || !profileFormChanged}
                      className={`w-full rounded-2xl px-4 py-3 text-sm font-bold transition-all ${
                        !savingProfile && profileFormChanged
                          ? "bg-[#274193] text-white hover:bg-[#1e3070]"
                          : "bg-[#e5e5ea] text-slate-500"
                      }`}
                    >
                      {savingProfile ? "Saving..." : "Save Changes"}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className={`rounded-3xl border p-4 ${isDark ? "border-white/10 bg-slate-900" : "border-slate-200 bg-white"}`}>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className={`text-base font-extrabold ${isDark ? "text-white" : "text-slate-900"}`}>Profile Details</p>
                    <p className={`mt-1 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                      Extra profile fields are hidden until you click edit details.
                    </p>
                  </div>
                  <button
                    onClick={() => setShowProfileExtra(true)}
                    className="rounded-2xl bg-[#274193] px-4 py-2 text-sm font-bold text-white transition hover:bg-[#1e3070]"
                  >
                    Edit Details
                  </button>
                </div>
              </div>
            )}

            <button
              onClick={handleLogout}
              className={`flex w-full items-center justify-between rounded-3xl border px-4 py-4 text-left transition ${isDark ? "border-red-500/30 bg-red-500/10 hover:bg-red-500/15" : "border-red-200 bg-red-50 hover:bg-red-100"}`}
            >
              <span className="inline-flex items-center gap-3 text-base font-bold text-red-500">
                <LogOut size={20} />
                Logout
              </span>
              <ChevronRight size={18} className="text-red-300" />
            </button>

            <p className={`text-center text-sm font-semibold ${isDark ? "text-slate-400" : "text-slate-400"}`}>v.1.0.43</p>
          </div>
        )}

          <AnimatePresence>
            {showPackagePicker && activeTab === "home" && serviceTab === "data" && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[70] bg-black/25"
              >
                <motion.div
                  initial={{ y: "100%" }}
                  animate={{ y: 0 }}
                  exit={{ y: "100%" }}
                  transition={{ type: "spring", damping: 30, stiffness: 260 }}
                  className="absolute bottom-0 left-0 right-0 mx-auto w-full max-w-md rounded-t-3xl border border-[#d8d8de] bg-[#f5f5f7]"
                >
                  <div className="flex items-center justify-between border-b border-[#dee0e6] px-4 py-4">
                    <h3 className="text-sm font-semibold text-slate-900">Select Package</h3>
                    <button
                      type="button"
                      onClick={() => setShowPackagePicker(false)}
                      className="rounded-full p-1 text-slate-500"
                      aria-label="Close package picker"
                    >
                      <X size={24} />
                    </button>
                  </div>

                  <div className="max-h-[58vh] overflow-y-auto pb-2">
                    {filteredPackages.map((pkg) => {
                      const selected = selectedPackageId === pkg.id;

                      return (
                        <button
                          key={pkg.id}
                          type="button"
                          onClick={() => {
                            setSelectedPackageId(pkg.id);
                            setShowPackagePicker(false);
                          }}
                          className={`flex w-full items-center justify-between border-b border-[#e3e5ea] px-4 py-2.5 text-left transition ${
                            selected ? "bg-[#e8eef8]" : "bg-[#f5f5f7]"
                          }`}
                        >
                          <div className="flex items-center gap-2.5">
                            <span
                              className={`flex h-5 w-5 items-center justify-center rounded-full border-2 ${
                                selected ? "border-[#2d5fd2] bg-[#2d5fd2] text-white" : "border-[#afb4be]"
                              }`}
                            >
                              {selected && <CheckCircle size={10} />}
                            </span>
                            <span className="text-sm font-semibold text-slate-800">{pkg.id}</span>
                          </div>

                          <span className="text-sm font-semibold text-slate-800">{formatGhs(pkg.promoPrice)}</span>
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              </motion.div>
            )}

            {showNotifications && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[80] flex items-end justify-center bg-black/60 p-4 backdrop-blur-sm"
              >
                <motion.div
                  initial={{ scale: 0.95, y: 100 }}
                  animate={{ scale: 1, y: 0 }}
                  exit={{ scale: 0.95, y: 100 }}
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  className={`w-full max-w-md rounded-t-3xl border shadow-2xl ${
                    isDark
                      ? "border-slate-700/50 bg-gradient-to-b from-slate-900 to-slate-800"
                      : "border-white/50 bg-gradient-to-b from-white to-slate-50/50"
                  } backdrop-blur-xl`}
                >
                  {/* Header */}
                  <div className="flex items-center justify-between border-b border-slate-200/20 p-6 dark:border-slate-700/20">
                    <div>
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-2 rounded-full bg-gradient-to-r from-blue-500 to-blue-600"></div>
                        <p className="text-xs font-bold uppercase tracking-[0.15em] text-blue-600 dark:text-blue-400">
                          Notifications
                        </p>
                      </div>
                      <h2 className={`mt-1 text-xl font-black ${isDark ? "text-white" : "text-slate-900"}`}>
                        Admin Updates
                      </h2>
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => setShowNotifications(false)}
                      className={`rounded-2xl border p-3 transition-all ${
                        isDark
                          ? "border-slate-600 bg-slate-800/50 hover:bg-slate-700/50"
                          : "border-slate-200 bg-white hover:bg-slate-50"
                      }`}
                    >
                      <X size={18} className={isDark ? "text-slate-400" : "text-slate-500"} />
                    </motion.button>
                  </div>

                  {/* Content */}
                  <div className="max-h-[60vh] overflow-y-auto p-6">
                    {loadingNotifications && (
                      <div className="flex items-center justify-center py-8">
                        <div className="flex items-center gap-3">
                          <div className="h-5 w-5 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
                          <span className={`text-sm font-medium ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                            Loading notifications...
                          </span>
                        </div>
                      </div>
                    )}

                    {!loadingNotifications && notifications.length === 0 && (
                      <div className="flex flex-col items-center justify-center py-12 text-center">
                        <div className={`mb-4 flex h-16 w-16 items-center justify-center rounded-3xl ${
                          isDark ? "bg-slate-800/50" : "bg-slate-100"
                        }`}>
                          <Bell size={32} className={isDark ? "text-slate-500" : "text-slate-400"} />
                        </div>
                        <h3 className={`text-lg font-bold mb-2 ${isDark ? "text-white" : "text-slate-900"}`}>
                          No notifications yet
                        </h3>
                        <p className={`text-sm ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                          You'll see important updates here when available.
                        </p>
                      </div>
                    )}

                    {!loadingNotifications && notifications.length > 0 && (
                      <div className="space-y-4">
                        {notifications.map((item, index) => (
                          <motion.div
                            key={item.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1 }}
                            className={`relative overflow-hidden rounded-2xl border p-4 shadow-lg ${
                              isDark
                                ? "border-slate-700/50 bg-gradient-to-r from-slate-800/50 to-slate-900/50"
                                : "border-white/50 bg-gradient-to-r from-white to-slate-50/30"
                            } backdrop-blur-sm`}
                          >
                            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 via-transparent to-purple-500/5" />

                            <div className="relative">
                              <div className="flex items-start gap-3">
                                <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${
                                  isDark ? "bg-slate-700/50" : "bg-slate-100"
                                }`}>
                                  <Bell size={18} className={isDark ? "text-blue-400" : "text-blue-600"} />
                                </div>
                                <div className="flex-1">
                                  <h4 className={`font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
                                    {item.title || "Admin Update"}
                                  </h4>
                                  <p className={`mt-1 text-sm leading-relaxed ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                                    {item.message || ""}
                                  </p>
                                  <div className="mt-3 flex items-center gap-2">
                                    <Clock size={14} className={isDark ? "text-slate-500" : "text-slate-400"} />
                                    <span className={`text-xs font-medium ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                                      {getTimeValue(item.createdAt)
                                        ? new Date(getTimeValue(item.createdAt)).toLocaleString()
                                        : "Just now"
                                      }
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    )}
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
      </div>

        <BottomNav activeTab={activeTab} setActiveTab={setActiveTab} isDark={isDark} />
    </div>
  );
}
